import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Plus, Users, RefreshCw, Plane, Wallet, CalendarClock, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";
import ClienteDetail from "@/pages/clientes/ClienteDetail";

const LIMIT = 25;
const EMPTY = { full_name: "", email: "", phone: "", country: "", language: "", assigned_specialist: "", status: "prospect" };

export const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const StatusBadge = ({ status }) =>
  status === "cliente"
    ? <Badge className="bg-emerald-600 hover:bg-emerald-600" data-testid="cliente-status">Cliente</Badge>
    : <Badge variant="secondary" data-testid="cliente-status">Prospect</Badge>;

export default function ClientesPage() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    const t = setTimeout(() => { setQ(term); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [term]);

  const listQ = useQuery({
    queryKey: ["clientes", q, statusF, page],
    queryFn: async () => (await api.get("/clientes", { params: { q, status: statusF === "all" ? "" : statusF, skip: page * LIMIT, limit: LIMIT } })).data,
    keepPreviousData: true,
  });

  const items = listQ.data?.items || [];
  const counts = listQ.data?.counts || { prospect: 0, cliente: 0, total: 0 };
  const total = listQ.data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  const syncMut = useMutation({
    mutationFn: () => api.post("/clientes/sync"),
    onSuccess: (res) => {
      toast.success(`Sincronizado: ${res.data.total} clientes (${res.data.nuevos} nuevos)`);
      qc.invalidateQueries({ queryKey: ["clientes"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const createMut = useMutation({
    mutationFn: () => api.post("/clientes", form),
    onSuccess: () => {
      toast.success("Cliente creado");
      qc.invalidateQueries({ queryKey: ["clientes"] });
      setOpen(false); setForm(EMPTY);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  if (selectedId) {
    return <ClienteDetail cid={selectedId} onBack={() => { setSelectedId(null); qc.invalidateQueries({ queryKey: ["clientes"] }); }} />;
  }

  return (
    <div className="space-y-5" data-testid="clientes-page">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight">
            <Users className="h-6 w-6 text-primary" /> Clientes (CRM)
          </h1>
          <p className="text-sm text-muted-foreground">
            Ficha unificada · {counts.total} fichas · {counts.cliente} clientes · {counts.prospect} prospects · Iteración 7
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => syncMut.mutate()} disabled={syncMut.isPending} data-testid="clientes-sync-btn">
            <RefreshCw className={`mr-1.5 h-4 w-4 ${syncMut.isPending ? "animate-spin" : ""}`} /> Sincronizar
          </Button>
          <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-cliente-button">
            <Plus className="mr-1.5 h-4 w-4" /> Nuevo cliente
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative sm:max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar por nombre o email…" className="pl-8" data-testid="cliente-search" />
        </div>
        <Select value={statusF} onValueChange={(v) => { setStatusF(v); setPage(0); }}>
          <SelectTrigger className="sm:w-44" data-testid="cliente-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="cliente">Clientes</SelectItem>
            <SelectItem value="prospect">Prospects</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <div className="space-y-2 p-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-12 text-center" data-testid="clientes-empty">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground"><Users className="h-6 w-6" /></div>
              <p className="font-semibold">Sin clientes todavía</p>
              <p className="max-w-sm text-sm text-muted-foreground">Pulsa <b>Sincronizar</b> para derivar fichas de los viajes y cotizaciones existentes, o crea una nueva.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="clientes-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Travel Specialist</TableHead>
                    <TableHead className="text-center">Viajes</TableHead>
                    <TableHead className="text-right">Gasto total</TableHead>
                    <TableHead>Último viaje</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((c) => (
                    <TableRow key={c.id} className="cursor-pointer" onClick={() => setSelectedId(c.id)} data-testid={`cliente-row-${c.id}`}>
                      <TableCell className="font-medium">
                        {c.full_name}
                        {c.kind === "acompanante" && <Badge variant="outline" className="ml-2 text-[10px]">acompañante</Badge>}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{c.email || "—"}</TableCell>
                      <TableCell><StatusBadge status={c.status} /></TableCell>
                      <TableCell className="text-muted-foreground">{c.assigned_specialist || "—"}</TableCell>
                      <TableCell className="text-center">{c.stats?.num_trips || 0}</TableCell>
                      <TableCell className="text-right font-medium">{eur(c.stats?.total_spend)}</TableCell>
                      <TableCell className="text-muted-foreground">{fmtDate(c.stats?.last_trip_date)}</TableCell>
                      <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {pages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
            <Button variant="outline" size="sm" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
          </div>
          <span className="text-muted-foreground">{total} fichas · página {page + 1}/{pages}</span>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cliente-create-dialog">
          <DialogHeader><DialogTitle>Nuevo cliente</DialogTitle><DialogDescription>Crea una ficha de cliente. Solo el nombre es obligatorio.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2"><Label>Nombre completo *</Label><Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="cliente-form-name" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="cliente-form-email" /></div>
            <div className="space-y-1.5"><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="cliente-form-phone" /></div>
            <div className="space-y-1.5"><Label>País</Label><Input value={form.country} onChange={(e) => set("country", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Idioma</Label><Input value={form.language} onChange={(e) => set("language", e.target.value)} placeholder="EN / ES…" /></div>
            <div className="space-y-1.5"><Label>Travel Specialist</Label><Input value={form.assigned_specialist} onChange={(e) => set("assigned_specialist", e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label>Estado</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="cliente-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prospect">Prospect</SelectItem>
                  <SelectItem value="cliente">Cliente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => createMut.mutate()} disabled={!form.full_name || createMut.isPending} data-testid="cliente-form-save">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
